'use client';

import Link from 'next/link';
import { SubmitButton } from '@/components/ui/submit-button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useState, useEffect, Suspense } from 'react';
import { signIn, signUp, forgotPassword, signInWithMagicLink } from './actions';
import { useSearchParams, useRouter } from 'next/navigation';
import {
  ArrowLeft,
  X,
  CheckCircle,
  AlertCircle,
  MailCheck,
  Loader2,
  Mail,
  KeyRound,
} from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { useAuthMethodTracking } from '@/lib/stores/auth-tracking';
import { toast } from 'sonner';
import { useFeatureFlag } from '@/lib/feature-flags';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { OtterzLogo } from '@/components/sidebar/otterz-logo';
import { Ripple } from '@/components/ui/ripple';
import { ReleaseBadge } from '@/components/auth/release-badge';
import { OAuthSignIn } from '@/components/auth/oauth-sign-in';

function LoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, isLoading } = useAuth();
  const mode = searchParams.get('mode');
  const returnUrl = searchParams.get('returnUrl');
  const message = searchParams.get('message');
  const invitationToken = searchParams.get('invitation_token');
  const { enabled: customAgentsEnabled } = useFeatureFlag("custom_agents");

  const isSignUp = mode === 'signup';
  const isInvitedSignup = isSignUp && !!invitationToken;
  const [mounted, setMounted] = useState(false);

  const { wasLastMethod: wasEmailLastMethod, markAsUsed: markEmailAsUsed } = useAuthMethodTracking('email');

  useEffect(() => {
    if (!isLoading && user) {
      router.push(returnUrl || '/dashboard');
    }
  }, [user, isLoading, router, returnUrl]);

  const isSuccessMessage =
    message &&
    (message.includes('Check your email') ||
      message.includes('Account created') ||
      message.includes('success'));

  // Registration success state
  const [registrationSuccess, setRegistrationSuccess] =
    useState(!!isSuccessMessage);
  const [registrationEmail, setRegistrationEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [useMagicLink, setUseMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');

  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordStatus, setForgotPasswordStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isSuccessMessage) {
      setRegistrationSuccess(true);
    }
  }, [isSuccessMessage]);

  const handleSignIn = async (prevState: any, formData: FormData) => {
    markEmailAsUsed();

    if (returnUrl) {
      formData.append('returnUrl', returnUrl);
    } else {
      formData.append('returnUrl', '/dashboard');
    }
    const result = await signIn(prevState, formData);

    if (
      result &&
      typeof result === 'object' &&
      'success' in result &&
      result.success &&
      'redirectTo' in result
      ) {
      window.location.href = result.redirectTo as string;
      return null;
    }

    if (result && typeof result === 'object' && 'message' in result) {
      toast.error('Login failed', {
        description: result.message as string,
        duration: 5000,
      });
      return {};
    }

    return result;
  };

  const handleSignUp = async (prevState: any, formData: FormData) => {
    markEmailAsUsed();

    // Validate terms acceptance
    if (!termsAccepted) {
      toast.error('Please accept the terms', {
        description: 'You must agree to the Terms & Conditions to create an account.',
        duration: 5000,
      });
      return {};
    }

    const email = formData.get('email') as string;
    setRegistrationEmail(email);

    if (returnUrl) {
      formData.append('returnUrl', returnUrl);
    }

    // Add origin for email redirects
    formData.append('origin', window.location.origin);
    formData.append('termsAccepted', 'true');

    const result = await signUp(prevState, formData);

    // Check for success and redirectTo properties (direct login case)
    if (
      result &&
      typeof result === 'object' &&
      'success' in result &&
      result.success &&
      'redirectTo' in result
    ) {
      // Use window.location for hard navigation to avoid stale state
      window.location.href = result.redirectTo as string;
      return null; // Return null to prevent normal form action completion
    }

    // Check if registration was successful but needs email verification
    if (result && typeof result === 'object' && 'message' in result) {
      const resultMessage = result.message as string;
      if (resultMessage.includes('Check your email')) {
        setRegistrationSuccess(true);

        // Update URL without causing a refresh
        const params = new URLSearchParams(window.location.search);
        params.set('message', resultMessage);

        const newUrl =
          window.location.pathname +
          (params.toString() ? '?' + params.toString() : '');

        window.history.pushState({ path: newUrl }, '', newUrl);

        return result;
      } else {
        toast.error('Sign up failed', {
          description: resultMessage,
          duration: 5000,
        });
        return {};
      }
    }

    return result;
  };

  const handleMagicLink = async (prevState: any, formData: FormData) => {
    const email = formData.get('email') as string;
    setMagicLinkEmail(email);

    if (returnUrl) {
      formData.append('returnUrl', returnUrl);
    } else {
      formData.append('returnUrl', '/dashboard');
    }

    const result = await signInWithMagicLink(prevState, formData);

    if (result && typeof result === 'object' && 'success' in result && result.success) {
      setMagicLinkSent(true);
      return result;
    }

    if (result && typeof result === 'object' && 'message' in result) {
      toast.error('Magic link failed', {
        description: result.message as string,
        duration: 5000,
      });
      return {};
    }

    return result;
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setForgotPasswordStatus({});

    if (!forgotPasswordEmail || !forgotPasswordEmail.includes('@')) {
      setForgotPasswordStatus({
        success: false,
        message: 'Please enter a valid email address',
      });
      return;
    }

    const formData = new FormData();
    formData.append('email', forgotPasswordEmail);
    formData.append('origin', window.location.origin);

    const result = await forgotPassword(null, formData);

    setForgotPasswordStatus(result);
  };

  const resetRegistrationSuccess = () => {
    setRegistrationSuccess(false);
    // Remove message from URL and set mode to signin
    const params = new URLSearchParams(window.location.search);
    params.delete('message');
    params.set('mode', 'signin');

    const newUrl =
      window.location.pathname +
      (params.toString() ? '?' + params.toString() : '');

    window.history.pushState({ path: newUrl }, '', newUrl);

    router.refresh();
  };

  // Show loading spinner while checking auth state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Registration success view
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto">
          <div className="text-center">
            <div className="bg-green-50 dark:bg-green-950/20 rounded-full p-4 mb-6 inline-flex">
              <MailCheck className="h-12 w-12 text-green-500 dark:text-green-400" />
            </div>

            <h1 className="text-3xl font-semibold text-foreground mb-4">
              Check your email
            </h1>

            <p className="text-muted-foreground mb-2">
              We've sent a confirmation link to:
            </p>

            <p className="text-lg font-medium mb-6">
              {registrationEmail || 'your email address'}
            </p>

            <div className="bg-green-50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/50 rounded-lg p-4 mb-8">
              <p className="text-sm text-green-800 dark:text-green-400">
                Click the link in the email to activate your account. If you don't see the email, check your spam folder.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Link
                href="/auth"
                className="flex h-11 items-center justify-center px-6 text-center rounded-lg border border-border bg-background hover:bg-accent transition-colors"
              >
                Return to sign in
              </Link>
              <button
                onClick={resetRegistrationSuccess}
                className="flex h-11 items-center justify-center px-6 text-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
      <div className="min-h-screen bg-background relative">
        <div className="absolute top-6 left-6 z-10">
          <Link href="/" className="flex items-center">
            <OtterzLogo size={28} />
          </Link>
        </div>
        <div className="flex min-h-screen">
          <div className="relative flex-1 flex items-center justify-center p-4 lg:p-8">
            <div className="absolute top-6 right-10 z-10">
              <Link
                href="/dashboard"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to dashboard
              </Link>
            </div>
            <div className="w-full max-w-sm">
              <div className="mb-4 flex items-center flex-col gap-4 justify-center">
                {customAgentsEnabled && <ReleaseBadge className='mb-4' text="Custom Agents, Workflows, and more!" link="/changelog" />}
                <h1 className="text-2xl font-semibold text-foreground">
                  {isSignUp ? 'Create your account' : 'Log into your account'}
                </h1>
              </div>
            {/* OAuth sign-in */}
            {!(useMagicLink && magicLinkSent) && (
              <div className="mt-4">
                <div className="space-y-2">
                  {(['google', 'microsoft'] as const).map((provider) => (
                    <OAuthSignIn
                      key={provider}
                      provider={provider}
                      returnUrl={returnUrl || undefined}
                      invitationToken={invitationToken || undefined}
                      onBeforeSignIn={() => {
                        if (isSignUp && !termsAccepted) {
                          toast.error('Please accept the terms', {
                            description:
                              'You must agree to the Terms & Conditions to create an account.',
                            duration: 5000,
                          });
                          return false;
                        }
                        return true;
                      }}
                    />
                  ))}
                </div>
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-background px-2 text-muted-foreground">
                      or
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Magic link sent confirmation */}
            {!isSignUp && useMagicLink && magicLinkSent ? (
              <div className="mt-4 text-center">
                <div className="bg-green-50 dark:bg-green-950/20 rounded-full p-3 mb-4 inline-flex">
                  <MailCheck className="h-10 w-10 text-green-500 dark:text-green-400" />
                </div>
                <h2 className="text-lg font-medium mb-2">Check your email</h2>
                <p className="text-sm text-muted-foreground mb-1">
                  We sent a magic login link to:
                </p>
                <p className="font-medium mb-4">{magicLinkEmail}</p>
                <div className="bg-green-50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-green-800 dark:text-green-400">
                    Click the link in the email to sign in. Check your spam folder if you don't see it.
                  </p>
                </div>
                <button
                  onClick={() => { setMagicLinkSent(false); setMagicLinkEmail(''); }}
                  className="text-sm text-primary hover:underline"
                >
                  Try a different email
                </button>
              </div>
            ) : !isSignUp && useMagicLink ? (
              /* Magic link form */
              <form className="space-y-3 mt-4">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Email address"
                  className="h-10 rounded-lg"
                  required
                />
                <div className="pt-2">
                  <SubmitButton
                    formAction={handleMagicLink}
                    className="w-full h-10 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-lg"
                    pendingText="Sending magic link..."
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Send magic link
                  </SubmitButton>
                </div>
              </form>
            ) : (
              /* Standard email/password form */
              <form className="space-y-3 mt-4">
              {isSignUp && !isInvitedSignup && (
                <Input
                  id="businessName"
                  name="businessName"
                  type="text"
                  placeholder="Business name"
                  className="h-10 rounded-lg"
                  required
                />
              )}
              {isInvitedSignup && (
                <input type="hidden" name="invitationToken" value={invitationToken || ''} />
              )}
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Email address"
                className="h-10 rounded-lg"
                required
              />
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Password"
                className="h-10 rounded-lg"
                required
              />
              {isSignUp && (
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm password"
                  className="h-10 rounded-lg"
                  required
                />
              )}
              {isSignUp && (
                <div className="flex items-start gap-3 pt-1 pb-1">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                    className="mt-0.5 flex-shrink-0"
                  />
                  <div className="flex-1 -mt-0.5">
                    <Label
                      htmlFor="terms"
                      className="text-xs text-muted-foreground leading-relaxed cursor-pointer select-none block"
                    >
                      <div className="mb-1">I agree to Otterz's</div>
                      <div className="flex flex-col gap-0.5 ml-0">
                        <a
                          href="https://otterz.co/terms-conditions/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Terms & Conditions
                        </a>
                        <a
                          href="https://otterz.co/consulting-services-terms/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Consulting Services
                        </a>
                        <a
                          href="https://otterz.co/subscription-agreement/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Subscription Agreement
                        </a>
                        <a
                          href="https://otterz.co/business-tax-services-agreement/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Tax Services Agreement
                        </a>
                      </div>
                    </Label>
                  </div>
                </div>
              )}
              <div className="pt-2">
                <div className="relative">
                  <SubmitButton
                    formAction={isSignUp ? handleSignUp : handleSignIn}
                    className="w-full h-10 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-lg"
                    pendingText={isSignUp ? "Creating account..." : "Signing in..."}
                  >
                    {isSignUp ? 'Create account' : 'Sign in'}
                  </SubmitButton>
                  {wasEmailLastMethod && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-background shadow-sm">
                      <div className="w-full h-full bg-green-500 rounded-full animate-pulse" />
                    </div>
                  )}
                </div>
              </div>
            </form>
            )}

            {/* Magic link / password toggle — only on sign-in */}
            {!isSignUp && !magicLinkSent && (
              <div className="mt-3 flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => setUseMagicLink(!useMagicLink)}
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {useMagicLink ? (
                    <>
                      <KeyRound className="h-3.5 w-3.5" />
                      Sign in with password
                    </>
                  ) : (
                    <>
                      <Mail className="h-3.5 w-3.5" />
                      Sign in with magic link
                    </>
                  )}
                </button>
              </div>
            )}
            
            <div className="mt-4 space-y-3 text-center text-sm">
              {!isSignUp && (
                <button
                  type="button"
                  onClick={() => setForgotPasswordOpen(true)}
                  className="text-primary hover:underline"
                >
                  Forgot password?
                </button>
              )}
              
              <div>
                <Link
                  href={isSignUp 
                    ? `/auth${returnUrl ? `?returnUrl=${returnUrl}` : ''}`
                    : `/auth?mode=signup${returnUrl ? `&returnUrl=${returnUrl}` : ''}`
                  }
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {isSignUp 
                    ? 'Already have an account? Sign in' 
                    : "Don't have an account? Sign up"
                  }
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="hidden lg:flex flex-1 items-center justify-center bg-sidebar relative overflow-hidden">
          <div className="absolute inset-0">
            <Ripple />
          </div>
        </div>
      </div>
      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Reset Password</DialogTitle>
            </div>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <Input
              id="forgot-password-email"
              type="email"
              placeholder="Email address"
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              className="h-11 rounded-xl"
              required
            />
            {forgotPasswordStatus.message && (
              <div
                className={`p-3 rounded-md flex items-center gap-3 ${
                  forgotPasswordStatus.success
                    ? 'bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/50 text-green-800 dark:text-green-400'
                    : 'bg-destructive/10 border border-destructive/20 text-destructive'
                }`}
              >
                {forgotPasswordStatus.success ? (
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                )}
                <span className="text-sm">{forgotPasswordStatus.message}</span>
              </div>
            )}
            <DialogFooter className="gap-2">
              <button
                type="button"
                onClick={() => setForgotPasswordOpen(false)}
                className="h-10 px-4 border border-border bg-background hover:bg-accent transition-colors rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="h-10 px-4 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-md"
              >
                Send Reset Link
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Login() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      }
    >
      <LoginContent />
    </Suspense>
  );
}
